import type { SheetGeometry, SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew, sheetTextGeometryNew } from "building-types/views/sheetGeometryTypes";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import { degreeToRadian } from "common/utils/math";
import {
  TextAlignment,
  textAlignmentToXAnchor,
  TextVerticalAlignment,
  textVerticalAlignmentToYAnchor,
} from "common/utils/textAlignment";
import { polyLine2Rotate } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2Rotate } from "geom/vec2";
import { generateArrowheadGeometry } from "lib/house/annotation/arrowheadGeometry";
import { textRightSideUp } from "lib/house/annotation/tags/textRightSideUp";

interface PlanLabelProps {
  text: string;
  verticalAlignment: TextVerticalAlignment;
  rotationRads: number;
  offset: number;
}

function makePlanLabelGeometry(props: PlanLabelProps): SheetTextGeometry {
  const alignment = TextAlignment.CENTER;
  const { text, verticalAlignment } = props;

  // rotate offset around rotationRads
  const offset = vec2Rotate(0, props.offset, props.rotationRads);

  return sheetTextGeometryNew({
    text,
    x: offset[0],
    y: offset[1],
    rotation: -props.rotationRads,
    strokeWidth: ZERO_POINTS,
    alignment,
    verticalAlignment,
    placementXAnchor: textAlignmentToXAnchor(alignment),
    placementYAnchor: textVerticalAlignmentToYAnchor(verticalAlignment),
  });
}

interface SlopeTagProps {
  rotate: number;
  planRotationRads: number;
  slopeLabel: string;
  slope: string;
  simplified: boolean;
}

const slopeTagDefault: SlopeTagProps = {
  rotate: 0,
  slopeLabel: "SLOPE",
  slope: "4:12",
  planRotationRads: 0,
  simplified: false,
};

function makeSimplifiedSlopeTagGeometry(props: SlopeTagProps): SheetGeometry[] {
  const { rotate, planRotationRads } = props;

  const textAngle = textRightSideUp(rotate, planRotationRads) + 90;

  return [
    makePlanLabelGeometry({
      text: props.slope,
      rotationRads: degreeToRadian(rotate + textAngle),
      offset: 2,
      verticalAlignment: TextVerticalAlignment.TOP,
    }),
  ];
}

function makeFullSlopeTagGeometry(props: Required<SlopeTagProps>): SheetGeometry[] {
  const { rotate, planRotationRads } = props;

  const textAngle = textRightSideUp(rotate, planRotationRads);

  const rotationRads = degreeToRadian(props.rotate);

  const LINE_LEN = 15;
  const ARROWHEAD_WIDTH = 4.8;
  const ARROWHEAD_HEIGHT = 5.6;

  const rotatedPos = vec2Rotate(0, LINE_LEN + ARROWHEAD_HEIGHT, rotationRads);

  const arrowheadGeometry = generateArrowheadGeometry({
    position: [rotatedPos[0], rotatedPos[1]],
    rotationRads,
    width: ARROWHEAD_WIDTH,
    height: ARROWHEAD_HEIGHT,
    strokeOpacity: 0,
  });

  return [
    arrowheadGeometry,
    sheetPolylineGeometryNew({
      paths: [polyLine2Rotate(polyLine2New([0, -LINE_LEN, 0, LINE_LEN]), rotationRads)],
    }),
    makePlanLabelGeometry({
      text: props.slopeLabel,
      verticalAlignment: TextVerticalAlignment.BOTTOM,
      rotationRads: degreeToRadian(rotate + textAngle),
      offset: -2,
    }),
    makePlanLabelGeometry({
      text: props.slope,
      verticalAlignment: TextVerticalAlignment.TOP,
      rotationRads: degreeToRadian(rotate + textAngle),
      offset: 2,
    }),
  ];
}

export function generateSlopeTagSheetGeometries(props: Partial<SlopeTagProps>): SheetGeometry[] {
  const resolvedProps = { ...slopeTagDefault, ...props };
  if (props.simplified) {
    return makeSimplifiedSlopeTagGeometry(resolvedProps);
  } else {
    return makeFullSlopeTagGeometry(resolvedProps);
  }
}
